import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Inject, inject, LOCALE_ID, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreadcrumbComponent, UpkeepNavigationComponent } from '@libs/components/navigation';
import { CatalogSearchResultModel, ProductModel } from '@libs/shop/models';
import { NavItemModel} from '@libs/shared/models';
import { EnvironmentService } from '@libs/shared/common';
import { ProductListSkeletonComponent } from '../../catalog/product-list/product-list-skeleton/product-list-skeleton.component';
import { ProductBundleRepositoryService } from '../../services/product-bundle-repository.service';
import { ProductBundlesListComponent } from '../product-bundles-list/product-bundles-list.component';
import { CmsOutletComponent } from '../../cms-outlet/cms-outlet.component';

@Component({
  selector: 'ui-closed-promotion-bundles',
  templateUrl: './closed-promotion-bundles.component.html',
  styleUrls: ['./closed-promotion-bundles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BreadcrumbComponent,
    ProductListSkeletonComponent,
    CmsOutletComponent,
    UpkeepNavigationComponent,
    ProductBundlesListComponent,
  ],
})
export class ClosedPromotionBundlesComponent {
  protected readonly productBundleRepository: ProductBundleRepositoryService = inject(ProductBundleRepositoryService);
  protected readonly env: EnvironmentService = inject(EnvironmentService);

  protected isCepApp: Signal<boolean> = computed(() => this.env.app().context === undefined);

  protected products: Signal<CatalogSearchResultModel | undefined> = toSignal(this.productBundleRepository.getProductBundles());

  protected productBundlesNavigationTree: Signal<NavItemModel[] | undefined> = computed(() => {
    return this.products()?.products.map((product: ProductModel) => {
      let description: string | undefined = undefined;

      if (product.prices?.validTo) {
        const validTo: string = formatDate(product.prices.validTo, 'dd.MM.yyyy', this.locale);

        description = `bis ${validTo}`;
      }

      return {
        id: product.sku,
        label: product.details.name,
        description: description,
        url: this.formatProductUrl(product),
      };
    });
  });

  protected navigationTree: Signal<NavItemModel[]> = computed(() => {
    return [
      {
        id: 'paket-aktionen',
        label: 'Paket-Aktionen',
        url: '/paket-aktionen',
        children: [
          {
            id: 'alle-paket-aktionen',
            label: 'Alle Paket-Aktionen',
            url: '/paket-aktionen',
            selected: true,
            children: this.productBundlesNavigationTree(),
          },
        ],
      },
    ];
  });

  protected breadcrumb: Signal<NavItemModel[]> = computed(() => {
    return [
      {
        id: 'startseite',
        label: 'Startseite',
        url: '/',
      },
      {
        id: 'paket-aktionen',
        label: 'Paket-Aktionen',
        url: '/paket-aktionen',
      },
      {
        id: 'alle-paket-aktionen',
        label: 'Alle Paket-Aktionen',
        url: '/paket-aktionen',
      },
    ];
  });

  public constructor(@Inject(LOCALE_ID) private readonly locale: string) {
  }

  public formatProductUrl(product: ProductModel): string {
    return this.isCepApp() ? `/product/${product.sku}` : String(product.details.link);
  }
}
